import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import ProductInfoContainer from 'pricing-pages-lib/js/components/ProductInfoContainer.tsx';
import { getStarterUpgradeProduct } from 'pricing-pages-lib/utils/getUpgradeProduct';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import Raven from 'raven-js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import BannerSection from '../components/banners/BannerSection';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import IntegrationsSection from '../components/common/IntegrationsSection';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import PhoneSupportMiddle from '../components/common/PhoneSupportMiddle';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import ErrorBoundary from '../components/error-boundary/ErrorBoundary';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import Faq from '../components/faqs/Faq';
import Question from '../components/faqs/Question';
import ProductFeatureTable from '../components/feature-table/ProductFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import HubTierInfo from '../components/merch-card/HubTierInfo';
import { PRODUCT_PAGE, PRODUCT_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { DEFAULT, OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { SCREENS } from '../constants/tracking/Screens';
import { TRACKING_SOURCES } from '../constants/tracking/TrackingSources';
import { ProductPageContext } from '../context/ProductPageContext';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePageParams } from '../hooks/usePageParams';
import { useAppSelector } from '../reducers';
import { isUngatedForInbound25Selector } from '../selectors/GatingSelectors';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { navItemForProductLineSelector } from '../selectors/RoutingSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useDocumentTitle(productLine.toLowerCase());
  const screen = SCREENS[productLine];
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const isUngatedForInbound25 = useSelector(isUngatedForInbound25Selector);
  const {
    tab
  } = usePageParams();
  useEffect(() => {
    if (!screen) {
      Raven.captureMessage('ProductPage screen is undefined', {
        extra: {
          productLine
        }
      });
      return;
    }
    trackPageView({
      screen,
      tab
    });
  }, [productLine, screen, tab]);
  const highlightedProducts = getHighlightsForProductLine(productLine);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const navItem = useAppSelector(state => navItemForProductLineSelector(state, productLine));
  const faqsConfig = FaqsConfig[navItem];
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  const upgradeProduct = getStarterUpgradeProduct({
    productLine,
    tab
  });
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: PRODUCT_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs("div", {
      "data-selenium-id": "page-success",
      children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
        name: `${productLine}_PAGE`
      }), /*#__PURE__*/_jsx(HeaderLayout, {
        selectedNavItem: navItem,
        children: /*#__PURE__*/_jsx(ProductInfoContainer, {
          children: /*#__PURE__*/_jsxs(ProductPageContext.Provider, {
            value: {
              highlightedProducts: filteredHighlightedProducts
            },
            children: [/*#__PURE__*/_jsx(BannerSection, {
              productLine: productLine,
              screen: screen
            }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
              children: [/*#__PURE__*/_jsx(ProductPageTitle, {
                productLine: productLine
              }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
                productLine: productLine
              })]
            }), /*#__PURE__*/_jsx(HubTierInfo, {
              productLine: productLine,
              selectedTab: tab
            }), isUngatedForInbound25 && /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(CreditsCallout, {
                navItem: navItem
              })
            }), /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(ProductFeatureTable, {
                productLine: productLine,
                highlightedProducts: filteredHighlightedProducts,
                screen: screen,
                tab: tab
              }, `${productLine}-${tab}`)
            }), /*#__PURE__*/_jsx(PhoneSupportMiddle, {
              phoneSourceId: TRACKING_SOURCES[`${productLine}_MIDDLE`] || TRACKING_SOURCES.PRODUCT_PAGE,
              screen: screen,
              upgradeProduct: upgradeProduct
            }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
              publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
              publicComponentProps: {
                tab
              }
            }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
              publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/case-studies/CaseStudies`),
              publicComponentProps: {
                productLine,
                tab
              }
            }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsx(Faq, {
              children: faqsConfig.map(_ref => {
                let {
                    questionKey
                  } = _ref,
                  passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
                return /*#__PURE__*/_jsx(Question, Object.assign({
                  questionKey: questionKey
                }, passthroughOptions), questionKey);
              })
            }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
              phoneSourceId: TRACKING_SOURCES[`${productLine}_FOOTER`] || TRACKING_SOURCES.PRODUCT_PAGE,
              screen: screen,
              upgradeProduct: upgradeProduct
            })]
          })
        })
      })]
    })
  });
};
export default withErrorBoundary(ProductPage, {
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  errorBoundaryName: PRODUCT_PAGE,
  severity: ERROR_SEVERITY.HIGH
});